import { ExpressionSyntaxError } from "building-types/errors/buildingGenerationError";
import type { Formula } from "building-types/options/formula";
import type { Expression } from "expr-eval";
import { parseFormula } from "./formulaParsing";
import { DEFAULT_IDENTIFIER_PREFIX } from "./formulaUtils";

export type FormulaEvalPathSegment = string | number;
export type FormulaEvalPath = readonly FormulaEvalPathSegment[];

export function formatFormulaEvalPath(path: FormulaEvalPath): string {
  if (path.length === 0) {
    return "";
  }

  let result = "";
  for (let i = 0; i < path.length; i++) {
    const segment = path[i];
    if (typeof segment === "number") {
      result += `[${segment}]`;
    } else if (result === "") {
      result = segment;
    } else {
      result += `.${segment}`;
    }
  }
  return result;
}

export type FormulaEvalContextInput = string | FormulaEvalPath;

function resolveFormulaEvalContextString(context: FormulaEvalContextInput | undefined): string {
  if (context === undefined) {
    return "";
  }
  if (typeof context === "string") {
    return context;
  }
  return formatFormulaEvalPath(context);
}

export function evalFormula(
  v: Formula,
  context: FormulaEvalContextInput = "",
  env: (value: string, formula: Formula) => any = () => undefined,
  contextualError: (e: { type: any; context: string }, context: FormulaEvalContextInput) => any = () => undefined,
) {
  const prefixStrippedEnv = (v: string, f: Formula) => {
    if (!v.startsWith(DEFAULT_IDENTIFIER_PREFIX)) {
      return env(v, f);
    } else {
      return env(v.slice(DEFAULT_IDENTIFIER_PREFIX.length), f);
    }
  };

  let expr: Expression;
  try {
    expr = parseFormula(v.formula);
  } catch (e) {
    throw new ExpressionSyntaxError(v.formula, resolveFormulaEvalContextString(context), e.message);
  }

  const variables = expr.variables();
  const formulaEnv = {};
  for (const variable of variables) {
    try {
      let value = prefixStrippedEnv(variable, v as Formula);

      if (value === undefined) {
        value = null;
      }
      formulaEnv[variable] = value;
    } catch (e) {
      contextualError(e, context);
    }
  }

  return expr.evaluate(formulaEnv);
}

export function evalFormulaAsNumber(
  v: Formula,
  context?: FormulaEvalContextInput,
  env?: (value: string, formula: Formula) => any,
  contextualError?: (e: { type: any; context: string }, context: FormulaEvalContextInput) => any,
) {
  const res = evalFormula(v, context, env, contextualError);
  if (typeof res === "number") {
    return res;
  }
  throw new Error(`Expected number, got ${res}`);
}
